/**
 * Functions to insert a hole in a BRep by ray intersection from one Face to a backing Face.
 * @module
 */
import type { BRep, Face, FaceTopo } from "./brep";
import { classifyContainmentPolyLine2PolyLine2, PolyLine2PolyLine2ContainmentClass } from "./containment";
import { WindingDir } from "./core";
import { extrudeEdge } from "./extrude";
import { intersectFaceRay } from "./intersect";
import { planeEval, planeNormal } from "./plane";
import { polyLine2Copy, polyLine2EdgeCount, polyLine2Reverse, polyLine2WindingDir } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import { vec3Dist } from "./vec3";

export function hole(
  brep: BRep,
  holeFaces: Face[],
  frontFaceIndex: number,
  holeOutline: PolyLine2,
  errs: string[],
): BRep | null {
  holeOutline = polyLine2Copy(holeOutline);
  if (polyLine2WindingDir(holeOutline) === WindingDir.CCW) {
    polyLine2Reverse(holeOutline);
  }

  const frontFace = brep.faces[frontFaceIndex];
  if (!frontFace) {
    errs.push("Invalid front face index");
    return null;
  }
  const frontPlane = frontFace.plane;

  if (!classifyContainmentFacePolyLine2(holeOutline, frontFace)) {
    errs.push("The hole is not completely within the face. ");
    return null;
  }

  const [fnormX, fnormY, fnormZ] = planeNormal(frontPlane);

  const minDists = new Float64Array(holeOutline.length / 2);
  minDists.fill(100000);
  const closestFaceIdxs = new Uint32Array(holeOutline.length / 2);
  const backHoleUVs = new Float64Array(holeOutline.length);

  const ignore = { [frontFaceIndex]: true };

  for (let i = 0, l = holeOutline.length; i < l; i += 2) {
    const i2 = i / 2;
    const [rayOriginX, rayOriginY, rayOriginZ] = planeEval(frontPlane, holeOutline[i], holeOutline[i + 1]);

    for (let j = 0; j < brep.faces.length; j++) {
      if (ignore[j]) {
        continue;
      }

      const f = brep.faces[j];
      const res = intersectFaceRay(f, rayOriginX, rayOriginY, rayOriginZ, fnormX, fnormY, fnormZ);

      if (res === null) {
        // if any one ray doesn't intersect the face, ignore it in all future loops
        ignore[j] = true;
        continue;
      }

      const [u, v] = res;
      const [x, y, z] = planeEval(f.plane, u, v);

      const d = vec3Dist(x, y, z, rayOriginX, rayOriginY, rayOriginZ);

      if (d < minDists[i2]) {
        backHoleUVs[i] = u;
        backHoleUVs[i + 1] = v;
        minDists[i2] = d;
        closestFaceIdxs[i2] = j;
      }
    }
  }

  const closedFaceIdx = closestFaceIdxs[0];
  for (let i = 1, l = closestFaceIdxs.length; i < l; i++) {
    if (closestFaceIdxs[i] !== closedFaceIdx) {
      errs.push("Cutting holes through multiple back faces are not supported.");
      return null;
    }
  }

  const backFaceIndex = closestFaceIdxs[0];
  return holeWithBackFaceIndexInternal(
    brep,
    holeFaces,
    frontFaceIndex,
    backFaceIndex,
    holeOutline,
    minDists,
    backHoleUVs,
  );
}

function holeWithBackFaceIndexInternal(
  brep: BRep,
  holeFaces: Face[],
  frontFaceIndex: number,
  backFaceIndex: number,
  holeOutline: PolyLine2,
  minDists: Float64Array,
  backHoleUVs: PolyLine2,
): BRep {
  const frontFace = brep.faces[frontFaceIndex];
  const backFace = brep.faces[backFaceIndex];
  const backFaceTopo = brep.faceTopos[backFaceIndex];
  const frontFaceTopo = brep.faceTopos[frontFaceIndex];
  const faceOffset = brep.faces.length; // start index of faces from this loop

  const [fnormX, fnormY, fnormZ] = planeNormal(frontFace.plane);

  function insertInnerLoopTopo(
    topo: FaceTopo,
    innerLoopPl: PolyLine2,
    sideFaceEdgeIndex: number,
    startFaceIndex: number,
  ) {
    let neighborFaceIndex = startFaceIndex;

    const loopTopos = new Int32Array(polyLine2EdgeCount(innerLoopPl) * 3);
    topo.loopTopos.push(loopTopos);

    for (let j = 0, lj = loopTopos.length; j < lj; j += 3) {
      loopTopos[j] = neighborFaceIndex;
      loopTopos[j + 1] = 0; // the side faces have a single outer loop
      loopTopos[j + 2] = sideFaceEdgeIndex;
      neighborFaceIndex++;
    }
  }

  // hole side topo

  backFace.loops.push(polyLine2Reverse(backHoleUVs));
  frontFace.loops.push(holeOutline);

  insertInnerLoopTopo(frontFaceTopo, holeOutline, 0, faceOffset);
  insertInnerLoopTopo(backFaceTopo, backHoleUVs, 2, faceOffset);

  // hole side geo

  const innerLoopIndex = frontFace.loops.length - 1;
  const innerLoop = frontFace.loops[innerLoopIndex];

  for (let j = 0, l = innerLoop.length - 2; j < l; j += 2) {
    const j2 = j / 2;
    const dist0 = minDists[j2];
    const dist1 = minDists[j2 + 1];

    extrudeEdge(
      frontFace,
      -fnormX,
      -fnormY,
      -fnormZ,
      innerLoopIndex,
      j,
      faceOffset,
      frontFaceIndex,
      backFaceIndex,
      holeFaces,
      [],
      dist0,
      dist1,
    );
  }

  return brep;
}

export function holeWithBackFaceIndex(
  brep: BRep,
  holeFaces: Face[],
  frontFaceIndex: number,
  backFaceIndex: number,
  holeOutline: PolyLine2,
): BRep | null {
  const frontFace = brep.faces[frontFaceIndex];
  const frontPlane = frontFace.plane;
  const backFace = brep.faces[backFaceIndex];

  const [fnormX, fnormY, fnormZ] = planeNormal(frontPlane);

  const minDists = new Float64Array(holeOutline.length / 2);
  minDists.fill(100000);
  const backHoleUVs = new Float64Array(holeOutline.length);

  for (let i = 0, l = holeOutline.length; i < l; i += 2) {
    const i2 = i / 2;
    const [rayOriginX, rayOriginY, rayOriginZ] = planeEval(frontPlane, holeOutline[i], holeOutline[i + 1]);

    const res = intersectFaceRay(backFace, rayOriginX, rayOriginY, rayOriginZ, fnormX, fnormY, fnormZ);
    if (res === null) {
      return null;
    }

    const [u, v] = res;
    const [x, y, z] = planeEval(backFace.plane, u, v);

    const d = vec3Dist(x, y, z, rayOriginX, rayOriginY, rayOriginZ);

    if (d < minDists[i2]) {
      backHoleUVs[i] = u;
      backHoleUVs[i + 1] = v;
      minDists[i2] = d;
    }
  }

  return holeWithBackFaceIndexInternal(
    brep,
    holeFaces,
    frontFaceIndex,
    backFaceIndex,
    holeOutline,
    minDists,
    backHoleUVs,
  );
}

function classifyContainmentFacePolyLine2(pl: PolyLine2, face: Face): boolean {
  const loops = face.loops;

  if (classifyContainmentPolyLine2PolyLine2(pl, loops[0]) !== PolyLine2PolyLine2ContainmentClass.INSIDE) {
    return false;
  }

  for (let i = 1, l = loops.length; i < l; i++) {
    if (classifyContainmentPolyLine2PolyLine2(pl, loops[i]) !== PolyLine2PolyLine2ContainmentClass.OUTSIDE) {
      return false;
    }
  }

  return true;
}
